import React, { useEffect, useState, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { motion } from "framer-motion";
import {
  BookOpen,
  Twitter,
  Menu,
  ArrowLeftCircle,
  ChevronRight
} from "lucide-react";
import Logo from "./assets/favicooon.png";
import ConnectWalletButton from "./components/ConnectWalletButton";

function App() {
  const isDesktop = useMediaQuery({ query: "(min-width: 768px)" });

  const [showCookiePopup, setShowCookiePopup] = useState(false);
  useEffect(() => {
    const hasAccepted = localStorage.getItem("cookiesAccepted");
    if (!hasAccepted) {
      setShowCookiePopup(true);
    }
  }, []);
  const handleAcceptCookies = () => {
    localStorage.setItem("cookiesAccepted", "true");
    setShowCookiePopup(false);
  };

  const API_BASE = process.env.REACT_APP_API_URL;
  const [conversations, setConversations] = useState([]);
  const [selectedConvoId, setSelectedConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");

  const ongoingRequests = useRef({});
  const dotIntervalRef = useRef(null);
  const baseTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [dotCount, setDotCount] = useState(0);
  const [baseIndex, setBaseIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const BASE_MESSAGES = [
    "Analyzing",
    "Thinking",
    "Fetching blockchain data",
    "Analyzing",
    "Gathering insights",
    "Tracking progress",
  ];

  const [showSidebar, setShowSidebar] = useState(true);

  const transformLinks = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    doc.querySelectorAll("a").forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });
    return doc.body.innerHTML;
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/conversations`, {
        credentials: "include",
      });
      const data = await res.json();
      setConversations((data.conversations || []));
    } catch (err) {
      console.error("Error fetching conversations:", err);
    }
  };
  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line
  }, []);


  const fetchConversationMessages = async (conversationId) => {
    if (!conversationId) return;
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${conversationId}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);

        if (ongoingRequests.current[conversationId]) {
          reinsertPlaceholderIfMissing();
          startDotInterval();
          scheduleNextBaseSwitch();
        }
      }
    } catch (err) {
      console.error("Error fetching conversation messages:", err);
    }
  };
  useEffect(() => {
    if (selectedConvoId) {
      fetchConversationMessages(selectedConvoId);
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line
  }, [selectedConvoId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCreateConversation = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mode: "Default" }),
      });
      const data = await res.json();
      await fetchConversations();
      setSelectedConvoId(data.conversation_id);
    } catch (err) {
      console.error("Error creating conversation:", err);
    }
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || !selectedConvoId) return;

    const userMsg = { role: "user", content: userInput.trim() };
    setMessages((prev) => [...prev, userMsg]);
    const userText = userInput.trim();
    setUserInput("");

    ongoingRequests.current[selectedConvoId] = true;
    setDotCount(0);
    setBaseIndex(0);

    setMessages((prev) => [
      ...prev,
      { role: "assistant", content: BASE_MESSAGES[0], isThinking: true },
    ]);
    startDotInterval();
    scheduleNextBaseSwitch();

    try {
      const res = await fetch(
        `${API_BASE}/api/conversations/${selectedConvoId}/chat`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userMessage: userText }),
        }
      );

      const data = await res.json();
      await fetchConversations();
      stopThinking();
      ongoingRequests.current[selectedConvoId] = false;

      if (data.assistantMessage) {
        const finalText = data.assistantMessage;
        setMessages((prev) => {
          const arr = [...prev];
          const i = arr.findIndex((m) => m.isThinking);
          if (i !== -1) {
            arr[i] = { role: "assistant", content: finalText };
          } else {
            arr.push({ role: "assistant", content: finalText });
          }
          return arr;
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      stopThinking();
    }
  };

  const reinsertPlaceholderIfMissing = () => {
    setMessages((prev) => {
      if (!prev.some((m) => m.isThinking)) {
        return [
          ...prev,
          { role: "assistant", content: BASE_MESSAGES[0], isThinking: true },
        ];
      }
      return prev;
    });
    setDotCount(0);
    setBaseIndex(0);
  };
  const startDotInterval = () => {
    clearInterval(dotIntervalRef.current);
    dotIntervalRef.current = setInterval(() => {
      setDotCount((c) => (c + 1) % 4);
    }, 800);
  };
  const scheduleNextBaseSwitch = () => {
    clearTimeout(baseTimeoutRef.current);
    const randDelay = 3500 + Math.floor(Math.random() * 2500);
    baseTimeoutRef.current = setTimeout(() => {
      setBaseIndex((b) => (b + 1) % BASE_MESSAGES.length);
      setMessages((prev) => {
        if (!prev.some((m) => m.isThinking)) return prev;
        scheduleNextBaseSwitch();
        return prev;
      });
    }, randDelay);
  };
  const stopThinking = () => {
    clearInterval(dotIntervalRef.current);
    dotIntervalRef.current = null;
    clearTimeout(baseTimeoutRef.current);
    baseTimeoutRef.current = null;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleTextareaChange = (e) => {
    setUserInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  const renderMessage = (msg, idx) => {
    if (msg.isThinking) {
      const dots = ".".repeat(dotCount);
      const finalText = BASE_MESSAGES[baseIndex] + dots;
      return (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="
            mb-3
            text-md
            text-gray-400
            whitespace-pre-wrap
            break-words
            break-all
          "
        >
          <strong>WALL-E:</strong> {finalText}
        </motion.div>
      );
    }

    if (msg.role === "user") {
      return (
        <motion.div
          key={idx}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="
            max-w-xl
            self-end
            mb-4
            px-4
            py-3
            bg-[#2a2a2a]
            text-gray-100
            shadow
            whitespace-pre-wrap
            break-words
            break-all
            border border-[#1a1a1a]
          "
        >
          {msg.content}
        </motion.div>
      );
    } else if (msg.role === "assistant") {
      const safeHTML = `<strong>WALL-E:</strong> ${transformLinks(msg.content)}`;
      return (
        <motion.div
          key={idx}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="
            max-w-xl
            self-start
            mb-4
            px-4
            py-3
            bg-[#1f1f1f]
            text-gray-100
            shadow
            whitespace-pre-wrap
            break-words
            break-all
            border border-[#1a1a1a]
          "
          dangerouslySetInnerHTML={{ __html: safeHTML }}
        />
      );
    }
    return null;
  };

  const renderConvoList = () => {
    return conversations.map((c) => {
      const isSelected = c.conversation_id === selectedConvoId;
      const displayName = c.name || c.conversation_id.slice(0, 6);

      return (
        <motion.li
          key={c.conversation_id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => {
            setSelectedConvoId(c.conversation_id);
          }}
          className={`
            cursor-pointer p-3 mb-1 transition-colors
            text-sm font-medium
            ${
              isSelected
                ? "bg-[#2a2a2a] text-white"
                : "bg-[#1f1f1f] text-gray-300 hover:bg-[#2a2a2a] hover:text-white"
            }
            /* Truncation classes: */
            overflow-hidden
            text-ellipsis
            whitespace-nowrap
            border border-[#1a1a1a]
          `}
          style={{ maxWidth: "100%" }}
        >
          {displayName} ({c.mode})
        </motion.li>
      );
    });
  };

  const sideBarX = isDesktop ? 0 : (showSidebar ? 0 : "-100%");

  const renderHomeLayout = () => (
    <>
      {/* Hero */}
      <header className="py-10 bg-[#0A0A0B] border-b border-[#1a1a1a]">
        <div className="container mx-auto px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4 text-white"
          >
            Welcome to{" "}
            <span className="bg-[linear-gradient(to_right,#01303f,#02577a,#02a9f7,#89d6fb,#d4f0fc)] bg-200% bg-clip-text text-transparent animate-gradient-shift">
              WALL-E CHAT
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl text-gray-400 max-w-3xl"
          >
            Your friendly on-chain assistant. Track wallets, check token security,
            and understand crypto movements in plain English.
          </motion.p>
        </div>
      </header>

      <div className="container mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-12 gap-8">
        <motion.aside
          initial={false}
          animate={{ x: sideBarX }}
          transition={{ type: "tween", duration: 0.3 }}
          className="
            md:col-span-4
            bg-[#0D0D0E]
            border border-[#1a1a1a]
            p-5
            flex flex-col
            max-h-[calc(100vh-25rem)]
            overflow-y-auto
            custom-scroll
            relative
            w-full
            md:w-auto
            max-w-[100%]
          "
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Conversations</h2>
            {!isDesktop && showSidebar && (
              <button
                className="text-gray-400 hover:text-white"
                onClick={() => setShowSidebar(false)}
              >
                <ArrowLeftCircle className="w-5 h-5" />
              </button>
            )}
          </div>

          <p className="text-sm text-gray-400 mb-3">
            Select an existing conversation or start a new one:
          </p>
          <div className="grid grid-cols-2 gap-2 mb-6">
            <button
              className="py-2 font-semibold
                         bg-[#02577a] text-white
                         hover:bg-[#02a9f7]
                         transition-colors text-sm 
                         border border-[#1a1a1a]"
              onClick={handleCreateConversation}
            >
              Default
            </button>
            <button
              className="py-2 font-semibold
                         bg-[#1f1f1f] text-gray-400
                         cursor-not-allowed text-sm
                         border border-[#1a1a1a]"
              title="Security mode is disabled in this demo"
            >
              Security
            </button>
            <button
              className="py-2 font-semibold
                         bg-[#1f1f1f] text-gray-400
                         cursor-not-allowed text-sm
                         border border-[#1a1a1a]"
              title="Investigation mode is disabled in this demo"
            >
              Investigation
            </button>
            <button
              className="py-2 font-semibold
                         bg-[#1f1f1f] text-gray-400
                         cursor-not-allowed text-sm
                         border border-[#1a1a1a]"
              title="Tracker mode is disabled in this demo"
            >
              Tracker
            </button>
          </div>

          <ul>{renderConvoList()}</ul>
        </motion.aside>

        <div className="md:col-span-8 flex items-center justify-center border border-dashed border-[#1a1a1a]">
          <p className="text-gray-400 text-center p-8 italic">
            No conversation selected.
          </p>
        </div>
      </div>
    </>
  );

  const renderFullScreenChat = () => (
    <div className="flex-1 flex overflow-hidden relative">
      {!isDesktop && showSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-30"
          onClick={() => setShowSidebar(false)}
        />
      )}

      <motion.aside
        initial={false}
        animate={{ x: sideBarX }}
        transition={{ type: "tween", duration: 0.3 }}
        className={`
          z-40
          ${isDesktop ? "static" : "fixed top-0 left-0"}
          h-full
          w-64
          bg-[#0D0D0E]
          border-r border-[#1a1a1a]
          p-5
          flex flex-col
          overflow-y-auto
          custom-scroll
        `}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Conversations</h2>
          {!isDesktop && (
            <button
              className="text-gray-400 hover:text-white"
              onClick={() => setShowSidebar(false)}
            >
              <ArrowLeftCircle className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-6">
          <button
            className="py-2 font-semibold
                      bg-[#02577a] text-white
                      hover:bg-[#02a9f7]
                      transition-colors text-sm
                      border border-[#1a1a1a]"
            onClick={handleCreateConversation}
          >
            Default
          </button>
          <button
            className="py-2 font-semibold
                      bg-[#1f1f1f] text-gray-400
                      cursor-not-allowed text-sm
                      border border-[#1a1a1a]"
            title="Security mode is disabled in this demo"
          >
            Security
          </button>
          <button
            className="py-2 font-semibold
                      bg-[#1f1f1f] text-gray-400
                      cursor-not-allowed text-sm
                      border border-[#1a1a1a]"
            title="Investigation mode is disabled in this demo"
          >
            Investigation
          </button>
          <button
            className="py-2 font-semibold
                      bg-[#1f1f1f] text-gray-400
                      cursor-not-allowed text-sm
                      border border-[#1a1a1a]"
            title="Tracker mode is disabled in this demo"
          >
            Tracker
          </button>
        </div>

        <ul>{renderConvoList()}</ul>
      </motion.aside>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden items-center bg-[#0A0A0B]">
        <div className="flex-1 w-full md:max-w-[75%] lg:max-w-[60%] xl:max-w-[45%] mx-auto overflow-y-auto px-4 py-12 flex flex-col custom-scroll">
          {messages.map((m, idx) => renderMessage(m, idx))}
          <div ref={messagesEndRef} />
        </div>

        <div className="text-center text-xs text-gray-500 mb-1">
          WALL-E is still in early versions. Don't rely solely on it for financial decisions.
        </div>

        <div className="w-full md:max-w-[75%] lg:max-w-[60%] xl:max-w-[45%] border-t border-[#1a1a1a] px-4 py-3">
          <div className="flex gap-2">
            <textarea
              className="
                flex-1
                resize-none
                bg-[#1f1f1f]
                border border-[#1a1a1a]
                p-3
                text-md
                text-gray-200
                focus:outline-none
                focus:border-[#02a9f7]
                custom-scroll
              "
              placeholder="Type your message..."
              style={{ minHeight: "3rem" }}
              value={userInput}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
            />
            <button
              className="inline-flex items-center justify-center bg-[#02577A] text-white px-4 py-2
                         text-sm font-medium hover:bg-[#02A9F7] transition-colors border border-[#1a1a1a] group"
              onClick={handleSendMessage}
            >
              <span>Send</span>
              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFlap = () => {
    if (isDesktop || showSidebar) return null;

    return (
      <div
        className="
          fixed
          top-1/5
          left-0
          w-10
          h-10
          bg-[#1f1f1f]
          text-white
          z-50
          flex
          items-center
          justify-center
          cursor-pointer
          border-r border-t border-b border-[#1a1a1a]
        "
        onClick={() => setShowSidebar(true)}
      >
        <Menu className="w-5 h-5" />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-[#0A0A0B] text-gray-200">
      <style jsx global>{`
        img {
          max-width: 100%;
          height: auto;
        }
        .custom-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: #1f1f1f;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background-color: #3a3a3a;
          border: 1px solid #1f1f1f;
        }
        .animate-gradient-shift {
          background-size: 200% auto;
          animation: gradientShift 15s ease infinite alternate;
        }
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>

      {showCookiePopup && (
        <motion.div
          initial={{ opacity: 0, x: 50, y: 50 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          className="fixed bottom-4 right-4 z-50"
        >
          <div className="w-80 p-4 bg-[#1f1f1f] border border-[#1a1a1a] shadow-lg space-y-2">
            <h3 className="text-lg font-semibold text-gray-100">Cookie Consent</h3>
            <p className="text-sm text-gray-400">
              We use cookies to ensure you get the best experience on our site. By
              continuing to browse, you accept our cookie policy.
            </p>
            <button
              onClick={handleAcceptCookies}
              className="px-4 py-2 bg-[#02577a] text-white text-sm border border-[#1a1a1a] hover:bg-[#02a9f7] transition-colors"
            >
              Accept
            </button>
          </div>
        </motion.div>
      )}

      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#0A0A0B]/90 backdrop-blur-md py-4' : 'py-6'
        }`}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src={Logo} alt="WALL-E Logo" className="w-8 h-8" />
              <span className="text-xl font-bold tracking-tight">WALL-E</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="https://docs.agentwall-e.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-[#02a9f7]" />
                  <span>Docs</span>
                </div>
              </a>
              <a
                href="https://x.com/agentwall_e"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Twitter className="w-5 h-5 text-[#02a9f7]" />
                  <span>Twitter</span>
                </div>
              </a>
              <ConnectWalletButton />
            </div>
          </div>
        </div>
      </nav>

      {renderFlap()}

      <div className="flex-1 flex overflow-hidden pt-16">
        {!selectedConvoId ? (
          <div className="w-full overflow-auto custom-scroll">
            {renderHomeLayout()}
          </div>
        ) : (
          renderFullScreenChat()
        )}
      </div>
    </div>
  );
}

export default App;